/**
 * Browser capability detection — which transports and hardware APIs the
 * current browser actually supports. Pages feature-gate with:
 *
 *   if (!granrojo.capabilities.transport("serial")) showFallbackHint();
 *   const caps = granrojo.capabilities.browser();  // { serial, bluetooth, ... }
 *
 * <gr-connect> uses this to gray out unavailable transports in the
 * protocol switcher instead of failing on connect.
 */

import { transportDefinition } from "../adapters/adapter";

/**
 * Whether a transport can work in this browser. Unknown transports are
 * assumed available — the registry may hold page-registered adapters the
 * SDK cannot probe.
 */
export function transportSupported(transport: string): boolean {
  const definition = transportDefinition(transport);
  return definition ? (definition.supported?.() ?? true) : false;
}

export interface BrowserCapabilities {
  serial: boolean;
  bluetooth: boolean;
  usb: boolean;
  hid: boolean;
  midi: boolean;
  gamepad: boolean;
  presentation: boolean;
  webrtc: boolean;
  /** AudioContext.setSinkId() — output device routing (Chromium). */
  sinkSelection: boolean;
}

/** Snapshot of the web-hardware APIs playgrounds commonly need. */
export function browserCapabilities(): BrowserCapabilities {
  const nav = typeof navigator !== "undefined" ? navigator : ({} as Navigator);
  return {
    serial: "serial" in nav,
    bluetooth: "bluetooth" in nav,
    usb: "usb" in nav,
    hid: "hid" in nav,
    midi: "requestMIDIAccess" in nav,
    gamepad: "getGamepads" in nav,
    presentation: "presentation" in nav,
    webrtc: typeof RTCPeerConnection !== "undefined",
    sinkSelection:
      typeof AudioContext !== "undefined" && "setSinkId" in AudioContext.prototype,
  };
}
